'use client';

import { c as _c } from "react/compiler-runtime";
import { Checkbox, Skeleton, TableCell, TableHead, TableRow, Typography, TableBody as MuiTableBody, Table as MuiTable, TableContainer, Divider, Stack, type SxProps, type Theme } from '@mui/material';
import { JobExecutionDetails } from '@repo/api-gw-sdk';
import { flexRender, useTable } from '@tanstack/react-table';
import type { ColumnPinningState, ColumnVisibilityState, ExpandedState, OnChangeFn, PaginationState, RowSelectionState, SortDirection, SortingState } from '@tanstack/react-table';
import classnames from 'classnames';
import React, { useMemo, useState, useEffect } from 'react';
import { useDAL } from '@/contexts/useDAL';
import { useEnvironment } from '@/contexts/useEnvironment';
import { PreferencesKey } from '@/data/dal/preferencesKey';
import { FoldableGroupHeading } from './foldableGroupHeading';
import { rowsVisibleAfterFolding, toggledGroupIds } from './groupFolding';
import type { PaginationBarAction } from './paginationBarActionTypes';
import tableStyles from './table.module.css';
import type { TableSettingsSource } from './tableSettings';
import { appTableFeatures } from './tanstackTable';
import type { AppCell, AppColumn, AppColumnDef, AppRow, AppTable as TableType, AppTableOptions } from './tanstackTable';
import { PanelEmptyState } from '../empty-state/panelEmptyState';
import { PaginationBar } from '../shared/paginationBar';
interface TableEmptyState {
  title?: React.ReactNode;
  description?: React.ReactNode;
  icon?: string | React.ReactNode;
  primaryAction?: React.ReactNode;
  secondaryAction?: React.ReactNode;
}
export interface TableGroupHeader<T extends object> {
  isGroupHeader: (row: T) => boolean;
  content: (row: T) => React.ReactNode;
  // Adds a chevron to each heading; clicking it hides the rows under that heading. Every group starts expanded and the
  // fold state is local to the table instance.
  isCollapsible?: boolean;
}
interface RowData<T extends object> {
  data: T[];
  isLoading: boolean;
  isMultiSelect: boolean;
  isRowSelectable?: (row: T) => boolean;
  MultiSelectActionButton?: (props: {
    entities: T[];
  }) => React.ReactNode;
  disableSorting?: boolean;
  sorting?: SortingState;
  columns: AppColumnDef<T>[];
  hiddenColumns?: string[];
  pagination?: {
    pageIndex: number;
    pageSize: number;
    recordsCount?: number;
    recordsLimit?: number;
    onChange: (pagination: PaginationState) => void;
    reachedLastPage?: boolean;
    totalSourceStorageSizeBytes?: number;
    // Overrides the left PaginationTitle text verbatim (see PaginationTitle's
    // titleOverride). For unknown-total (cursor) pagination where recordsCount
    // can't be supplied, this is the only way to show a real row range on a
    // title that would otherwise be blank — see GdsgObjectsBrowser for the
    // cumulative range it computes.
    displayedRangeLabel?: string;
    // Replaces the total in the right-hand "from–to of total" label, for a count that is known to be incomplete.
    totalLabel?: string;
  };
  paginationBarActions?: PaginationBarAction<T>[];
  paginationLeadingActions?: React.ReactElement[];
  paginationRowsPerPageOptions?: number[];
  // What the table puts in its settings panel beyond the page size. Every paginated table gets the panel: page size
  // lives in it and nowhere else.
  settings?: TableSettingsSource;
  // Draws the pagination bar's compact variant; for tables without row selection or bar actions.
  compactToolbar?: boolean;
  onSortingChanged?: (state: SortingState) => void;
  testId: string;
  entityLabel: string;
  stickyHeader?: boolean;
  // Empty-state copy shown in place of rows. Defaults to a generic message —
  // pass a domain-specific one when "no data" needs more context (e.g. an
  // active filter/prefix scoping the empty result).
  noDataMessage?: string;
  emptyState?: TableEmptyState;
  initialSelectedId?: string;
  onRowSelectionChange?: (entities: string[]) => void;
  enableColumnResizing?: boolean;
  columnVisibilityPersistence?: ColumnVisibilityPersistence;
  // Column ids that stay in view at the table's start or end edge while it scrolls sideways.
  pinnedColumns?: ColumnPinningState;
  // Rows for which `isGroupHeader` holds render as one full-width heading row with `content` instead of cells, so a flat
  // `data` array can carry its own group headings between the rows they introduce.
  groupHeader?: TableGroupHeader<T>;
  // Compact rows, header and group headings with no trailing space under the last row, for tables nested in a panel or card.
  // Columns keep their sizes as proportions of the width and cell text truncates instead of wrapping.
  dense?: boolean;
}
const NO_PINNED_COLUMNS: ColumnPinningState = {
  start: [],
  end: []
};

/**
 * Where the column visibility is saved; defaults to the testId. A table whose default layout changes moves to a new key,
 * and a reader with nothing saved there starts from the old key's choices minus the columns whose defaults changed, so
 * the new defaults show for those and every other choice survives.
 */
interface ColumnVisibilityPersistence {
  key: string;
  inheritFrom?: {
    key: string;
    exceptColumns: string[];
  };
}

// Accepts a getter instead of the table directly so the useMemo that builds
// extendedColumns can safely call createSelectionColumn(() => table) before
// `const table = useTable(...)` is reached. Creating the arrow function
// captures the binding without reading it (no TDZ access); the getter is only
// called inside onClick, at which point `table` is fully initialized.
function createSelectionColumn<T extends object>(getTable: () => TableType<T>): AppColumnDef<T> {
  return {
    id: 'select',
    enableHiding: false,
    size: 53,
    // getCanSelect() reflects isRowSelectable via enableRowSelection; non-selectable
    // rows (e.g. folders) get no checkbox at all rather than a disabled one.
    cell: ({
      row
    }: {
      row: AppRow<T>;
    }) => {
      if (!row.getCanSelect()) {
        return null;
      }
      return <Checkbox checked={row.getIsExpanded()} onClick={e => {
        if (!e.shiftKey || row.getIsExpanded()) {
          row.getToggleExpandedHandler()();
          return;
        }
        const table = getTable();
        const rows = table.getRowModel().rows;
        // table.setExpanded goes through onExpandedChange, which clears
        // rowSelection (closing the detail panel) the same way a normal
        // checkbox click does.
        table.setExpanded(computeShiftRangeExpanded(rows, row.index));
      }} />;
    }
  };
}
export const Table = <T extends object,>({
  data,
  hiddenColumns,
  isMultiSelect,
  isRowSelectable,
  MultiSelectActionButton,
  columns,
  sorting,
  isLoading,
  pagination,
  disableSorting,
  onSortingChanged,
  testId,
  entityLabel,
  stickyHeader,
  noDataMessage,
  emptyState,
  paginationBarActions,
  paginationLeadingActions,
  paginationRowsPerPageOptions,
  settings = {},
  compactToolbar,
  initialSelectedId,
  onRowSelectionChange,
  enableColumnResizing,
  columnVisibilityPersistence,
  pinnedColumns,
  groupHeader,
  dense
}: RowData<T>) => {
  const isFixedLayout = Boolean(enableColumnResizing) || Boolean(dense);
  const [collapsedGroupIds, setCollapsedGroupIds] = useState<ReadonlySet<string>>(new Set());
  const [isTableScrolledVertically, setTableScrolledVertically] = useState(false);
  const extendedColumns = useMemo<AppColumnDef<T>[]>(() => [...(isMultiSelect ? [createSelectionColumn<T>(() => table)] : []), ...columns],
  // `table` cannot be listed as a dep: extendedColumns feeds into tableOptions
  // which feeds into useTable, so adding table here creates a circular
  // dependency. It is safe to omit because TanStack returns the same object
  // reference every render (mutated in place) and the getter is only called
  // inside onClick handlers, never at useMemo evaluation time.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [isMultiSelect, columns]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(initialSelectedId ? {
    [initialSelectedId]: true
  } : {});
  const [expandedState, setExpandedState] = useState<ExpandedState>({});
  useEffect(() => {
    setRowSelection(initialSelectedId ? {
      [initialSelectedId]: true
    } : {});
  }, [initialSelectedId]);
  const {
    columnVisibility,
    onColumnVisibilityChange,
    resetColumnsVisibility,
    isColumnsVisibilityDefault
  } = usePersistedColumnsVisibility(columnVisibilityPersistence ?? {
    key: testId
  }, extendedColumns, hiddenColumns);
  const tableOptions: AppTableOptions<T> = {
    features: appTableFeatures,
    // `expanded` holds the row checkboxes here, not disclosure state, so it must outlive a data change. TanStack
    // otherwise clears it whenever `data` changes identity — which a refetch does on every mutation — and that reset
    // cascades through onExpandedChange into resetRowSelection, closing a detail panel the user still has open.
    autoResetExpanded: false,
    // Pinned alongside enableSortingRemoval below: onSortingChanged callers destructure the first entry, so an empty
    // sorting array throws. Nothing may hand them one.
    autoResetSorting: false,
    data,
    enableColumnResizing: Boolean(enableColumnResizing),
    columnResizeMode: enableColumnResizing ? 'onChange' : undefined,
    columns: extendedColumns,
    enableRowSelection: isRowSelectable ? row => isRowSelectable(row.original) : true,
    enableSortingRemoval: false,
    enableMultiRowSelection: isMultiSelect,
    getRowId: (row, index) => {
      if ('id' in row && typeof row.id === 'string') {
        return row.id;
      }
      if ('jobExecutionDetails' in row && row.jobExecutionDetails instanceof JobExecutionDetails) {
        return row.jobExecutionDetails.jobId;
      }
      return `${index}`;
    },
    state: {
      sorting,
      rowSelection,
      expanded: expandedState,
      columnVisibility,
      columnPinning: pinnedColumns ?? NO_PINNED_COLUMNS
    },
    onColumnVisibilityChange,
    getRowCanExpand: row => isMultiSelect && (isRowSelectable?.(row.original) ?? true),
    onRowSelectionChange: state => {
      const newState = typeof state === 'function' ? state(rowSelection) : state;
      setRowSelection(newState);
      onRowSelectionChange?.(Object.keys(newState));
      if (Object.keys(expandedState).length > 0) {
        table.resetExpanded(true);
      }
    },
    onExpandedChange: state => {
      const newState = typeof state === 'function' ? state(expandedState) : state;
      setExpandedState(newState);
      if (Object.keys(rowSelection).length > 0) {
        table.resetRowSelection(true);
      }
    },
    onSortingChange: state => {
      if (!disableSorting && sorting && onSortingChanged) {
        const newState = typeof state === 'function' ? state(sorting) : state;
        onSortingChanged(newState);
      }
    }
  };
  const table = useTable(tableOptions);
  const multiSelect = isMultiSelect ? {
    // table.getIsAllRowsExpanded/getToggleAllRowsExpandedHandler treat non-expandable
    // rows as already expanded (they blanket-set expanded to boolean `true`), so with
    // isRowSelectable present they'd report checked/select-all across non-selectable
    // rows too; the selectable-only variants below scope both to getCanExpand() rows.
    getIsAllRecordsSelected: isRowSelectable ? () => getIsAllSelectableRowsExpanded(table) : table.getIsAllRowsExpanded,
    getIsSomeRecordsSelected: table.getIsSomeRowsExpanded,
    getToggleAllRecordsSelectedHandler: isRowSelectable ? () => () => toggleAllSelectableRowsExpanded(table) : table.getToggleAllRowsExpandedHandler,
    ActionButton: MultiSelectActionButton ? <MultiSelectActionButton entities={getCheckedEntities(table)} /> : undefined
  } : undefined;
  const onPageChange = (page: number) => {
    pagination?.onChange({
      pageSize: pagination.pageSize,
      pageIndex: page
    });
  };
  return <>
      {compactToolbar && pagination && <PaginationBar variant='compact' entityLabel={entityLabel} isLoading={isLoading} pagination={pagination} onPageChange={onPageChange} pageSizeControl={{
      pageSize: pagination.pageSize,
      rowsPerPageOptions: paginationRowsPerPageOptions,
      onPageSizeChange: (pageSize, pageIndex) => pagination.onChange({
        pageSize,
        pageIndex
      })
    }} settings={settings} />}
      {!compactToolbar && (isMultiSelect || pagination || paginationBarActions?.length || paginationLeadingActions?.length) && <>
            <Divider />
            <PaginationBar pagination={pagination} onPageChange={onPageChange} entityLabel={entityLabel} selection={{
        multiSelect,
        selectedRecordsCount: getCheckedEntities(table).length
      }} isLoading={isLoading} totalSourceStorageSizeBytes={pagination?.totalSourceStorageSizeBytes} leadingActions={paginationLeadingActions} settings={settings} pageSizeControl={pagination && {
        pageSize: pagination.pageSize,
        rowsPerPageOptions: paginationRowsPerPageOptions,
        onPageSizeChange: (pageSize, pageIndex) => pagination.onChange({
          pageSize,
          pageIndex
        })
      }} actions={paginationBarActions?.map(Component => <Component key={Component.displayName ?? Component.name} columns={table.getAllColumns()} resetColumnsVisibility={resetColumnsVisibility} isColumnsVisibilityDefault={isColumnsVisibilityDefault} />)} />
          </>}
      <TableContainer sx={{
      minHeight: '0px',
      flexGrow: 1,
      overflow: 'auto'
    }} onScroll={event => {
      const target = event.target as HTMLDivElement;
      if (typeof target.scrollTop === 'number') {
        const isScrolled = target.scrollTop > 0;
        if (isScrolled !== isTableScrolledVertically) {
          setTableScrolledVertically(isScrolled);
        }
      }
    }}>
        <MuiTable className={classnames(tableStyles.table, {
        [tableStyles.dense]: dense
      })} data-testid={testId} stickyHeader={stickyHeader} sx={isFixedLayout ? {
        tableLayout: 'fixed',
        width: '100%'
      } : undefined}>
          <TableHead>
            {table.getHeaderGroups().map(headerGroup => <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => <TableCell key={header.id} sx={getColumnStyles(header.column, isFixedLayout, 'head')} className={classnames({
              'cursor-pointer select-none': !disableSorting && header.column.getCanSort(),
              [tableStyles.sorted]: header.column.getIsSorted(),
              [tableStyles.scrollingVertically]: isTableScrolledVertically
            })} onClick={!disableSorting && header.column.getCanSort() ? () => {
              header.column.toggleSorting();
            } : undefined}>
                    {header.isPlaceholder ? null : <>
                        <Stack direction='row' sx={{
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                          <Typography variant={dense ? 'subtitle2Strong' : 'overline'}>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </Typography>
                          {header.column.getCanSort() && <SortIcon isSorted={header.column.getIsSorted()} />}
                        </Stack>
                        {enableColumnResizing && <div onMouseDown={header.getResizeHandler()} onTouchStart={header.getResizeHandler()} className={tableStyles.resizer} />}
                      </>}
                  </TableCell>)}
              </TableRow>)}
          </TableHead>
          <TableBody<T> isLoading={isLoading} isMultiSelect={isMultiSelect} table={table} isFixedLayout={isFixedLayout} isDense={Boolean(dense)} collapsedGroupIds={collapsedGroupIds} onToggleGroup={groupId => setCollapsedGroupIds(current => toggledGroupIds(current, groupId))} isRowSelected={row => row.getIsExpanded() || row.getIsSelected()} noDataMessage={noDataMessage} emptyState={emptyState} groupHeader={groupHeader} />
        </MuiTable>
      </TableContainer>
    </>;
};
const LoadingState = t0 => {
  const $ = _c(5);
  const {
    numOfRows,
    numOfColumns
  } = t0;
  let t1;
  if ($[0] !== numOfRows) {
    t1 = [...Array(numOfRows)];
    $[0] = numOfRows;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== numOfColumns || $[3] !== t1) {
    t2 = <MuiTableBody data-testid="table-loading">{t1.map((_, i) => <TableRow key={i}>{[...Array(numOfColumns)].map(_temp)}</TableRow>)}</MuiTableBody>;
    $[2] = numOfColumns;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
const NoDataState = t0 => {
  const $ = _c(7);
  const {
    columnCount,
    message,
    emptyState
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      whiteSpace: "normal"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== emptyState || $[2] !== message) {
    t2 = emptyState ? <PanelEmptyState {...emptyState} description={emptyState.description ?? message} minHeight="120px" sx={{
      padding: "24px 16px"
    }} /> : message ?? "No data available";
    $[1] = emptyState;
    $[2] = message;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== columnCount || $[5] !== t2) {
    t3 = <MuiTableBody data-testid="table-empty"><TableRow><TableCell colSpan={columnCount} className="text-center" sx={t1}>{t2}</TableCell></TableRow></MuiTableBody>;
    $[4] = columnCount;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
function TableBody(t0) {
  const $ = _c(23);
  const {
    table,
    isLoading,
    isMultiSelect,
    isFixedLayout,
    isDense,
    collapsedGroupIds,
    onToggleGroup,
    isRowSelected,
    noDataMessage,
    emptyState,
    groupHeader
  } = t0;
  if (isLoading) {
    let t1;
    if ($[0] !== table) {
      t1 = table.getVisibleFlatColumns();
      $[0] = table;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1.length) {
      t2 = <LoadingState numOfRows={10} numOfColumns={t1.length} />;
      $[2] = t1.length;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  if (table.getRowModel().rows.length === 0) {
    let t1;
    if ($[4] !== table) {
      t1 = table.getVisibleFlatColumns();
      $[4] = table;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    let t2;
    if ($[6] !== emptyState || $[7] !== noDataMessage || $[8] !== t1.length) {
      t2 = <NoDataState columnCount={t1.length} message={noDataMessage} emptyState={emptyState} />;
      $[6] = emptyState;
      $[7] = noDataMessage;
      $[8] = t1.length;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    return t2;
  }
  let T0;
  let t1;
  if ($[10] !== collapsedGroupIds || $[11] !== groupHeader || $[12] !== isDense || $[13] !== isFixedLayout || $[14] !== isMultiSelect || $[15] !== isRowSelected || $[16] !== onToggleGroup || $[17] !== table) {
    const visibleRows = groupHeader?.isCollapsible ? rowsVisibleAfterFolding(table.getRowModel().rows, {
      isGroupHeader: row => groupHeader.isGroupHeader(row.original),
      idOf: _temp2
    }, collapsedGroupIds) : table.getRowModel().rows;
    T0 = MuiTableBody;
    t1 = visibleRows.map(row_1 => {
      if (groupHeader?.isGroupHeader(row_1.original)) {
        const isCollapsed = collapsedGroupIds.has(row_1.id);
        return <TableRow key={row_1.id} data-testid="table-group-header"><TableCell colSpan={table.getVisibleFlatColumns().length} className={classnames({
            [tableStyles.collapsibleGroupHeader]: groupHeader.isCollapsible
          })} sx={{
            whiteSpace: "normal",
            backgroundColor: isDense ? "var(--mui-palette-action-hover)" : "var(--mui-palette-background-section)"
          }}>{groupHeader.isCollapsible ? <FoldableGroupHeading isCollapsed={isCollapsed} onToggle={() => onToggleGroup(row_1.id)}>{groupHeader.content(row_1.original)}</FoldableGroupHeading> : groupHeader.content(row_1.original)}</TableCell></TableRow>;
      }
      return <TableRow key={row_1.id} data-selected={isRowSelected(row_1)} className={classnames({
        [tableStyles.selected]: isRowSelected(row_1)
      })}>{row_1.getVisibleCells().map(cell => <TableCell key={cell.id} sx={getColumnStyles(cell.column, isFixedLayout, "body")} className={classnames({
          [tableStyles.multi]: isMultiSelect
        })}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>)}</TableRow>;
    });
    $[10] = collapsedGroupIds;
    $[11] = groupHeader;
    $[12] = isDense;
    $[13] = isFixedLayout;
    $[14] = isMultiSelect;
    $[15] = isRowSelected;
    $[16] = onToggleGroup;
    $[17] = table;
    $[18] = T0;
    $[19] = t1;
  } else {
    T0 = $[18];
    t1 = $[19];
  }
  let t2;
  if ($[20] !== T0 || $[21] !== t1) {
    t2 = <T0>{t1}</T0>;
    $[20] = T0;
    $[21] = t1;
    $[22] = t2;
  } else {
    t2 = $[22];
  }
  return t2;
}
function _temp2(row_0) {
  return row_0.id;
}
function computeShiftRangeExpanded<T extends object>(rows: AppRow<T>[], clickedIdx: number): Record<string, boolean> {
  // Seed with currently checked rows so table.setExpanded (a full replacement)
  // does not wipe them.
  const newExpanded = rows.filter(r => r.getIsExpanded()).reduce<Record<string, boolean>>((acc, r) => {
    acc[r.id] = true;
    return acc;
  }, {});
  const selectedIndexes = rows.filter(r => r.getIsExpanded()).map(r => r.index);
  if (selectedIndexes.length === 0) {
    newExpanded[rows[clickedIdx].id] = true;
    return newExpanded;
  }
  const closestIdx = selectedIndexes.reduce((best, idx) => Math.abs(idx - clickedIdx) < Math.abs(best - clickedIdx) ? idx : best);
  const rangeStart = Math.min(closestIdx, clickedIdx);
  const rangeEnd = Math.max(closestIdx, clickedIdx);
  for (let i = rangeStart; i <= rangeEnd; i++) {
    // Non-selectable rows (e.g. folders) can sit inside the range; they must
    // never enter the expanded map.
    if (rows[i].getCanExpand()) {
      newExpanded[rows[i].id] = true;
    }
  }
  return newExpanded;
}
function getIsAllSelectableRowsExpanded<T extends object>(table: TableType<T>): boolean {
  const selectableRows = table.getRowModel().rows.filter(row => row.getCanExpand());
  return selectableRows.length > 0 && selectableRows.every(row => row.getIsExpanded());
}
function toggleAllSelectableRowsExpanded<T extends object>(table: TableType<T>): void {
  const selectableRows = table.getRowModel().rows.filter(row => row.getCanExpand());
  if (getIsAllSelectableRowsExpanded(table)) {
    table.setExpanded({});
    return;
  }
  table.setExpanded(selectableRows.reduce<Record<string, boolean>>((acc, row) => {
    acc[row.id] = true;
    return acc;
  }, {}));
}
function getCheckedEntities<T extends object>(table: TableType<T>) {
  const selectedEntities = table.getRowModel().rows.filter(r => r.getIsExpanded()).map(r => r.original);
  return selectedEntities;
}
const PINNED_EDGE_SHADOW_COLOR = 'var(--mui-palette-divider)';

// A pinned cell sits over the columns scrolling beneath it, so it needs an opaque background, and the shadow on its
// inner edge marks where the scrolling columns pass under it.
const pinnedColumnStyles = <T extends object,>(column: AppColumn<T>, part: 'head' | 'body') => {
  const position = column.getIsPinned();
  if (!position) {
    return {};
  }
  const isInnerEdge = position === 'start' ? column.getIsLastColumn('start') : column.getIsFirstColumn('end');
  return {
    position: 'sticky',
    insetInlineStart: position === 'start' ? `${column.getStart('start')}px` : undefined,
    insetInlineEnd: position === 'end' ? `${column.getAfter('end')}px` : undefined,
    // Above the scrolling cells, and in the header above the sticky header's own cells.
    zIndex: part === 'head' ? 3 : 1,
    backgroundColor: part === 'head' ? 'var(--mui-palette-background-section)' : 'var(--cell-bg-color)',
    boxShadow: isInnerEdge ? `${position === 'start' ? '-' : ''}4px 0 4px -4px ${PINNED_EDGE_SHADOW_COLOR} inset` : undefined
  } as const;
};
const getColumnStyles = <T extends object,>(column: AppColumn<T>, isFixedLayout: boolean, part: 'head' | 'body'): SxProps<Theme> => {
  const size = column.getSize();
  if (isFixedLayout) {
    return {
      ...pinnedColumnStyles(column, part),
      width: size,
      maxWidth: size,
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      '& > *': {
        minWidth: 0,
        maxWidth: '100%',
        verticalAlign: 'middle'
      }
    };
  }
  return {
    ...pinnedColumnStyles(column, part),
    width: size
  };
};
const inheritedColumnVisibility = (tables: Record<string, {
  columnVisibility?: Record<string, boolean>;
}> | undefined, inheritFrom: ColumnVisibilityPersistence['inheritFrom']) => {
  const inherited = inheritFrom && tables?.[inheritFrom.key]?.columnVisibility;
  if (!inherited) {
    return undefined;
  }
  return Object.fromEntries(Object.entries(inherited).filter(([column]) => !inheritFrom.exceptColumns.includes(column)));
};
const usePersistedColumnsVisibility = <T extends object,>({
  key: tableKey,
  inheritFrom
}: ColumnVisibilityPersistence, columns: AppColumnDef<T>[] = [], defaultHiddenColumns: string[] = []) => {
  const dal = useDAL();
  const {
    isDemo,
    isDev
  } = useEnvironment();
  const shouldPersistColumnVisibility = !isDemo && !isDev;
  const {
    data: prefResponse
  } = dal.preferences.useUserPref(PreferencesKey.Preferences);
  const defaultColumnVisibility = useMemo(() => columns.reduce<Record<string, boolean>>((acc, col) => {
    const id = 'accessorKey' in col && col.accessorKey || col.id;
    if (typeof id === 'string') {
      acc[id] = !defaultHiddenColumns.includes(id);
    }
    return acc;
  }, {}), [columns, defaultHiddenColumns]);
  const [columnVisibility, setColumnVisibility] = useState(defaultColumnVisibility);
  const onColumnVisibilityChange: OnChangeFn<ColumnVisibilityState> = updater => {
    const newState = typeof updater === 'function' ? updater(columnVisibility) : updater;
    if (compareBooleanRecords(newState, columnVisibility)) {
      return;
    }
    setColumnVisibility(newState);
    if (!shouldPersistColumnVisibility) {
      return;
    }
    void dal.preferences.getUserPrefAsync(PreferencesKey.Preferences).then(perf => {
      const updatedValue = perf || {};
      updatedValue.value = updatedValue.value || {};
      updatedValue.value.tables = updatedValue.value.tables || {};
      updatedValue.value.tables[tableKey] = updatedValue.value.tables[tableKey] || {};
      const persistedValue = updatedValue.value.tables[tableKey].columnVisibility;
      if (!persistedValue && compareBooleanRecords(newState, defaultColumnVisibility) || compareBooleanRecords(newState, persistedValue || {})) {
        return;
      }
      updatedValue.value.tables[tableKey].columnVisibility = newState;
      return dal.preferences.updateUserPref(PreferencesKey.Preferences, updatedValue.value, true);
    });
  };
  useEffect(() => {
    const tables = prefResponse?.value?.tables;
    const persistedColumnVisibility = tables?.[tableKey]?.columnVisibility ?? inheritedColumnVisibility(tables, inheritFrom);
    if (!persistedColumnVisibility || !shouldPersistColumnVisibility) {
      return;
    }

    // A column that cannot be hidden keeps its default, so a layout saved while it was still hideable cannot hide it.
    const unhideableColumns = new Set(columns.filter(col => col.enableHiding === false).map(col => 'accessorKey' in col && col.accessorKey || col.id));
    const mergedColumnVisibility = Object.entries(columnVisibility).reduce<Record<string, boolean>>((acc, [key, value]) => {
      acc[key] = key in persistedColumnVisibility && !unhideableColumns.has(key) ? persistedColumnVisibility[key] : value;
      return acc;
    }, {});
    onColumnVisibilityChange(mergedColumnVisibility);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefResponse]);
  return {
    columnVisibility,
    onColumnVisibilityChange,
    resetColumnsVisibility: () => onColumnVisibilityChange(defaultColumnVisibility),
    isColumnsVisibilityDefault: compareBooleanRecords(columnVisibility, defaultColumnVisibility)
  };
};
function compareBooleanRecords(a: Record<string, boolean>, b: Record<string, boolean>): boolean {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) {
    return false;
  }
  for (const key of keysA) {
    if (a[key] !== b[key]) {
      return false;
    }
  }
  return true;
}
const SortIcon = t0 => {
  const $ = _c(2);
  const {
    isSorted
  } = t0;
  let t1;
  if ($[0] !== isSorted) {
    t1 = isSorted && <i className={classnames({
      "ri-arrow-down-s-line": isSorted === "desc",
      "ri-arrow-up-s-line": isSorted === "asc"
    })} />;
    $[0] = isSorted;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
function _temp(__0, j) {
  return <TableCell key={j} className="text-center"><Skeleton variant="text" /></TableCell>;
}